import logger from '../../../utils/logger-rum';
/* eslint-disable no-param-reassign */
import React, {
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import type { RemoteVideoTrack, RemoteAudioTrack } from 'livekit-client';
import { useLivekit } from '../../../contexts/livekit/livekit-context';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Hls from 'hls.js';
import type { OnsiteLocation, OnsiteLocationStream, SrtStream } from '../../../stores/types/video';
import type { DropCallback } from '../../../stores/types/user-interface';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import useDropArea from '../../../hooks/use-dnd/use-drop-area';
import DefaultSafetyImage from '../../../images/default-safety-image.png';
import { AVContext } from '../../../contexts/audio-video';
import blackImage from '../../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../../utils/video/video-streams';
import { GreenroomState as GS } from '../../../stores/types/types';
import useShotMemos from '../../../hooks/use-shot-memos';
import { ROOM_STATES } from '../../../stores/reducers/settings';
import useCanvasConfig from '../../../hooks/use-canvas-config';
import { RoomStates } from '../../../types/enums';
import useEchoCancellation, { shouldDoEchoCancel } from '../../../hooks/use-echo-cancellation';
import useAudioSink from '../../../hooks/devices/use-audio-sink';
import useLivekitAudioPlayback from '../../../hooks/use-livekit-audio-playback';
import useAdjustedVolume from '../../../hooks/use-adjusted-volume';
import { isLivekitIngressInputType } from '../../../stores/types/livekit';

const doEchoCancel = shouldDoEchoCancel();

interface LocationOpenTokStreamProps {
	otStream: OnsiteLocationStream | undefined;
	feedPosition: number;
	embed: boolean;
	backstageVolume: number;
	roomState: RoomStates;
	showingHoldSlide: boolean;
	isOnCanvas: boolean;
}

function LocationOpenTokStream(props: LocationOpenTokStreamProps) {
	const {
		otStream,
		feedPosition,
		embed,
		backstageVolume,
		roomState,
		showingHoldSlide,
		isOnCanvas,
	} = props;

	const videoElRef = useRef<HTMLVideoElement>(null);
	const videoDebounce = useRef<NodeJS.Timeout | null>(null);

	async function playMediaStream(srcObject: MediaStream) {
		if (!videoElRef.current) return;

		videoElRef.current.srcObject = srcObject;
		videoElRef.current.volume = 0;

		if (!srcObject.active) return;

		try {
			await videoElRef.current.play();
			if (videoElRef.current?.paused) {
				try {
					await videoElRef.current.play();
				} catch (e: any) {
					logger.error('error playing onsite location feed', e);
				}
			}
		} catch (e: any) {
			logger.error('error playing onsite location feed', e);
		}

		cleanupVideoStreamElement(videoElRef.current);
	}

	useEffect(() => {
		if (videoElRef.current && otStream && otStream?.srcObject !== videoElRef.current.srcObject) {
			if (videoDebounce.current) { clearTimeout(videoDebounce.current); }
			videoDebounce.current = setTimeout(() => {
				playMediaStream(otStream.srcObject);
			}, 100);
		}
	}, [otStream?.srcObject, videoElRef.current]);

	useEffect(() => {
		if (videoElRef.current) {
			if (showingHoldSlide) {
				videoElRef.current.volume = 0;
			} else if (isOnCanvas || embed || roomState === ROOM_STATES.EDITING) {
				videoElRef.current.volume = 1;
			} else {
				videoElRef.current.volume = backstageVolume;
			}
		}
	}, [showingHoldSlide, isOnCanvas, roomState, backstageVolume]);

	return (
		<video
			key={otStream?.srcObject ? (otStream.srcObject.id + feedPosition) : ''}
			muted={true}
			poster={blackImage}
			autoPlay={true}
			playsInline
			ref={videoElRef}
			onLoadedData={() => {
				videoElRef.current?.play().catch((e) => {
					logger.error('video el play request error', {
						location: 'feed-onsite-location',
					}, e);
				});
			}}
		/>
	);
}

interface LocationSrtStreamProps {
	srtStream: SrtStream | null | undefined;
	feedPosition: number;
	embed: boolean;
	adjustedVolume: number;
	muted: boolean;
	isForShotEditorModal: boolean;
}

function LocationSrtStream(props: LocationSrtStreamProps) {
	const {
		srtStream,
		feedPosition,
		embed,
		adjustedVolume,
		muted,
		isForShotEditorModal,
	} = props;

	const videoRef = useRef<HTMLVideoElement>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const hlsRef = useRef<Hls | null>(null);

	const { applySinkId } = useAudioSink();
	useEchoCancellation(videoRef, audioRef);

	useEffect(() => {
		if (videoRef?.current) {
			applySinkId(videoRef.current);
		}
	}, [applySinkId, videoRef?.current]);

	useEffect(() => {
		if (videoRef.current && srtStream) {
			const src = `https://stream.mux.com/${srtStream.playbackId}.m3u8${embed ? '?min_resolution=1080p' : ''}`;

			if (Hls.isSupported()) {
				hlsRef.current = new Hls();
				hlsRef.current.loadSource(src);
				hlsRef.current.attachMedia(videoRef.current);

				hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
					logger.error('location srt stream hls error', {
						location: 'feed-onsite-location',
						id: srtStream.id,
						playbackId: srtStream.playbackId,
						event,
						data,
					});
				});

				return () => {
					if (hlsRef.current) {
						hlsRef.current.destroy();
						hlsRef.current = null;
					}
				};
			}
		}

		return undefined;
	}, [srtStream?.playbackId, embed, srtStream?.id]);

	useEffect(() => {
		if (doEchoCancel && audioRef.current) {
			audioRef.current.volume = muted ? 0 : adjustedVolume;
		}
		if (!doEchoCancel && videoRef.current) {
			videoRef.current.volume = muted ? 0 : adjustedVolume;
		}
	}, [
		videoRef.current,
		audioRef.current,
		muted,
		adjustedVolume,
	]);

	return (
		srtStream ? (
			<>
				<video
					id={`srt-stream-${srtStream.id}-${feedPosition}`}
					ref={videoRef}
					playsInline={true}
					muted={false}
					loop={false}
					poster={srtStream.thumbnail}
					onLoadedData={() => {
						if (doEchoCancel && audioRef.current) {
							audioRef.current.volume = muted ? 0 : adjustedVolume;
						}
						if (!doEchoCancel && videoRef.current) {
							videoRef.current.volume = muted ? 0 : adjustedVolume;
						}
						if (videoRef.current) {
							videoRef.current?.play().catch((e) => {
								logger.error('location srt stream onLoadedData play error', {
									location: 'feed-onsite-location',
									id: srtStream.id,
									playbackId: srtStream.playbackId,
								}, e);
							});
						}
					}}
					onPause={() => {
						// always resume playing if paused
						if (videoRef.current && !videoRef.current.ended) {
							videoRef.current.play().catch((e) => {
								logger.error('location srt stream onPause play error', {
									location: 'feed-onsite-location',
									id: srtStream.id,
									playbackId: srtStream.playbackId,
								}, e);
							});
						}
					}}
				/>
				{doEchoCancel && (
					<audio
						id={`srt-audio-${srtStream.id}-${feedPosition}${isForShotEditorModal ? '-draft' : ''}`}
						ref={audioRef}
						is="advanced-audio"
					/>
				)}
			</>
		) : null
	);
}

interface LocationIngressStreamProps {
	videoTrack: RemoteVideoTrack;
	audioTrack: RemoteAudioTrack | null;
	feedPosition: number;
	muted: boolean;
	adjustedVolume: number;
}

function LocationIngressStream({
	videoTrack,
	audioTrack,
	feedPosition,
	muted,
	adjustedVolume,
}: LocationIngressStreamProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const audioRef = useRef<HTMLAudioElement>(null);

	useEffect(() => {
		const element = videoRef.current;
		if (!element) return;
		videoTrack.attach(element);
		return () => { videoTrack.detach(element); };
	}, [videoTrack]);

	// render the ingress's remote audio via Web Audio to keep it out of Chrome's WebRTC
	// render mix (prevents echo-cancel loopback bleed) — see the hook for details
	useLivekitAudioPlayback(audioTrack, audioRef, { muted, volume: adjustedVolume });

	return (
		<>
			<video
				key={`ingress-video-${feedPosition}`}
				ref={videoRef}
				autoPlay
				playsInline
				muted
				onPause={() => {
					// always resume playing if paused — e.g. after being staged
					// offscreen in spotlight before reaching the canvas
					if (videoRef.current && !videoRef.current.ended) {
						videoRef.current.play().catch((e) => {
							logger.error('location ingress stream onPause play error', {
								location: 'feed-onsite-location',
								feedPosition,
							}, e);
						});
					}
				}}
			/>
			{audioTrack && (
				<audio
					key={`ingress-audio-${feedPosition}`}
					ref={audioRef}
					autoPlay
					onPause={() => {
						// the spotlight system physically moves this element between
						// DOM containers on shot transitions, which pauses it — resume
						// so audio keeps playing across consecutive shots
						if (audioRef.current && !audioRef.current.ended) {
							audioRef.current.play().catch((e) => {
								logger.error('location ingress stream audio onPause play error', {
									location: 'feed-onsite-location',
									feedPosition,
								}, e);
							});
						}
					}}
				/>
			)}
		</>
	);
}

interface FeedOnsiteLocationProps {
	feedPosition: number;
	onsiteLocation: {
		data: OnsiteLocation | undefined;
		stream: OnsiteLocationStream | undefined;
	}
	isSpotlightFeed?: boolean;
	isForShotEditorModal?: boolean;
	dropCallback: DropCallback;
}

export default function FeedOnsiteLocation(props: FeedOnsiteLocationProps) {
	const {
		feedPosition,
		onsiteLocation,
		isSpotlightFeed = false,
		isForShotEditorModal = false,
		dropCallback,
	} = props;

	const {
		data,
		stream: onsiteSubscriberStream,
	} = onsiteLocation;

	// if on the onsite location canvas view and opened directly from an instance that is publishing,
	// attempt to use the MediaStream stored on the window object if it's the same onsite location
	const {
		onsitePublisherStream,
	} = window;

	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const livekitIngresses = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);
	const { use_whip_streams: useWhipStreams } = useSelector((s: GS) => s.SettingsReducer.featureFlags);
	const clearShot = useSelector((s: GS) => s.SettingsReducer.clearShot);
	const { participantsMap } = useLivekit();

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	const { getAV } = useContext(AVContext);
	const { video: hasVideo } = getAV(onsiteSubscriberStream?.id);
	const { currentShot, isOnCanvas } = useShotMemos();
	const { showingHoldSlide } = useCanvasConfig(currentShot);

	const otStream = (
		onsitePublisherStream
		&& onsitePublisherStream.srcObject.active
		&& onsitePublisherStream.onsiteLocationId === data?.id
	)
		? { id: onsitePublisherStream.srcObject.id, srcObject: onsitePublisherStream.srcObject }
		: onsiteSubscriberStream;

	const effectiveInputType = useMemo(() => {
		if (!data) return 'cam_mic';
		if (useWhipStreams && data.inputType) return data.inputType;
		return data.useSrt ? 'srt' : 'cam_mic';
	}, [useWhipStreams, data?.inputType, data?.useSrt]);

	// only attempt to find srt stream if there isn't an opentok stream and SRT is the selected input
	const srtStream = useMemo(
		() => (!otStream && data && data?.srtId && effectiveInputType === 'srt'
			? srtStreams.find((srt) => srt.id === data.srtId)
			: null),
		[otStream, srtStreams, data?.srtId, effectiveInputType],
	);

	const isActiveOTStream = useMemo(
		() => !!otStream && otStream.srcObject && otStream.srcObject.active,
		[otStream?.srcObject?.active],
	);

	const isActiveSrtStream = useMemo(
		() => !!srtStream && srtStream.status === 'active',
		[srtStream?.status],
	);

	const ingress = useMemo(() => {
		if (!useWhipStreams || !isLivekitIngressInputType(effectiveInputType)) return null;
		if (!data?.inputId) return null;
		return livekitIngresses.find((i) => i.id === data.inputId) ?? null;
	}, [useWhipStreams, effectiveInputType, data?.inputId, livekitIngresses]);

	const ingressStream = useMemo(() => {
		if (!ingress?.participantIdentity) return null;
		return participantsMap.get(ingress.participantIdentity) ?? null;
	}, [ingress, participantsMap]);

	const isActiveIngressStream = !!ingressStream;

	// only one of SRT/ingress renders at a time — gate whichever stream's base volume
	const adjustedVolume = useAdjustedVolume({
		baseVolume: isActiveSrtStream ? (srtStream?.volume ?? 0) : (ingress?.volume ?? 1),
		onsiteLocationId: data?.id,
		isSpotlightFeed,
		showingHoldSlide,
		embed,
		roomState,
		backstageVolume: backstageCanvasVolume,
		isActive: isActiveSrtStream || isActiveIngressStream,
		clearShot,
		isOnCanvas,
	});

	return (
		<>
			{isOver && <div className="dnd-highlighted" />}
			<div
				className="media"
				ref={dropRef}
			>
				<div className="flex-center height-full width-full">
					{isActiveOTStream && (
						<LocationOpenTokStream
							otStream={otStream}
							feedPosition={feedPosition}
							embed={embed}
							backstageVolume={backstageCanvasVolume}
							roomState={roomState}
							showingHoldSlide={showingHoldSlide}
							isOnCanvas={isOnCanvas}
						/>
					)}
					{!isActiveOTStream && isActiveSrtStream && (
						<LocationSrtStream
							srtStream={srtStream}
							feedPosition={feedPosition}
							embed={embed}
							adjustedVolume={adjustedVolume}
							muted={srtStream?.muted ?? false}
							isForShotEditorModal={isForShotEditorModal}
						/>
					)}
					{!isActiveOTStream && !isActiveSrtStream && ingressStream?.videoTrack && (
						<LocationIngressStream
							videoTrack={ingressStream.videoTrack}
							audioTrack={ingressStream.audioTrack}
							feedPosition={feedPosition}
							muted={!!ingress?.muted}
							adjustedVolume={adjustedVolume}
						/>
					)}
					<div className={classNames('poster', {
						active: (!isActiveOTStream && !isActiveSrtStream && !ingressStream?.videoTrack)
							|| (isActiveOTStream && !hasVideo) // active opentok stream with video off
							|| (isActiveSrtStream && srtStream?.videoMuted) // active srt stream with video off
							// active ingress stream with video off
							|| (isActiveIngressStream && !!ingress?.videoMuted),
					})}>
						<img
							className="poster-image"
							src={data?.safetyImage || DefaultSafetyImage}
							alt="Safety Image"
							onError={({ currentTarget }) => {
								currentTarget.src = DefaultSafetyImage;
							}}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
